import type { ToolOutput } from '../../services/api'
import { Chart } from '../../ds'
import { partialBuckets } from './chartData'

interface ChartMessageProps {
  output: ToolOutput
}

const CHART_TYPES = ['line', 'bar', 'area', 'pie'] as const
type ChartType = (typeof CHART_TYPES)[number]

/**
 * A chart an answer carries, drawn by the design system's `Chart` (D-37): the
 * `chart-1…5` tokens in both themes, one y-axis, at most five series then Other,
 * a gap where a bucket is missing (never a zero), partial periods marked and
 * named, stacking only over complete series, and a table view for every chart.
 *
 * ⚠️ merchant-chat's `salesChart` sends `null` for a bucket a currency's series
 * does not carry. That is MISSING, not zero, and it stays `null` on the way in:
 * a line through 0 there would be a sales figure nobody reported.
 */
const ChartMessage = ({ output }: ChartMessageProps) => {
  if (!output.data?.labels || !output.data?.datasets) {
    return null
  }

  const { chartType = 'line', title, data, options } = output
  const type: ChartType = (CHART_TYPES as readonly string[]).includes(chartType) ? (chartType as ChartType) : 'line'

  return (
    <Chart
      type={type}
      title={title}
      labels={data.labels}
      series={data.datasets.map((dataset) => ({
        label: dataset.label,
        data: data.labels.map((_, i) => {
          const value = dataset.data[i]
          return typeof value === 'number' && Number.isFinite(value) ? value : null
        }),
      }))}
      partial={partialBuckets(data)}
      prefix={options?.valuePrefix || ''}
      suffix={options?.valueSuffix || ''}
      stacked={!!options?.stacked}
      legend={options?.showLegend === false ? false : undefined}
    />
  )
}

export default ChartMessage
